import type { Message } from '../types/chat';
import { defaults } from '../messages';

// Tool calls that are UI notifications only, not sent to API
const UI_ONLY_TOOLS = ['_meiliSearchProgress', '_meiliSearchSources'];

/**
 * Formats a message for the API by removing UI-specific fields
 * @param message The message to format
 * @returns Message formatted for API consumption
 */
function formatMessageForAPI(message: Message): any {
    const apiMessage: any = {
        role: message.role,
        content: message.content,
    };

    if (message.tool_calls?.length) {
        const apiToolCalls = message.tool_calls.filter(
            (tc) => !UI_ONLY_TOOLS.includes(tc.function.name)
        );
        if (apiToolCalls.length > 0) {
            apiMessage.tool_calls = apiToolCalls;
        }
    }

    if (message.tool_call_id) {
        apiMessage.tool_call_id = message.tool_call_id;
    }

    return apiMessage;
}

/**
 * Filters messages for display in the UI
 * Excludes internal messages that are only used for context
 * @param messages Array of all messages
 * @returns Messages that should be displayed to the user
 */
export function filterForDisplay(messages: Message[]): Message[] {
    return messages.filter(message => !message.internal);
}

/**
 * Filters messages for sending to the chat API
 * Includes all non-internal messages and only internal messages from the most recent search
 * When a remediation message is present (e.g., after hallucination detection), filters out
 * all internal messages before it to drastically reduce context
 * This prevents context bloat from accumulated search results
 * @param messages Array of all messages
 * @returns Messages formatted for the API
 */
export function filterForChatAPI(messages: Message[]): any[] {
    // Find last remediation message (internal assistant message after hallucination)
    const lastRemediationIndex = messages.findLastIndex(
        message => message.internal && message.role === 'assistant' && message.content === defaults.remediation
    );

    const lastInternalIndex = messages.findLastIndex(message => message.internal);
    const lastAssistantWithSearches = messages
        .slice(0, lastInternalIndex + 1)
        .findLastIndex(message => message.role === 'assistant' && !message.internal);

    const omitInternalMessagesBeforeIndex = Math.max(lastRemediationIndex, lastAssistantWithSearches);

    return messages
        .filter((message, index) => {
            if (!message.internal) {
                return true;
            }

            return omitInternalMessagesBeforeIndex === -1 || index >= omitInternalMessagesBeforeIndex;
        })
        .map(formatMessageForAPI);
}
